import {
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UserCircleLight } from '@quizzach/assets';
import { useAuth, useCartContext } from '@quizzach/contexts';
import { ICart, RoutePath } from '@quizzach/interfaces';
import { BtnTeal } from '@quizzach/styles';
import {
	GIFT_BASKET_STORAGE_KEY,
	GiftLine,
	consoleDev,
	ConsoleStatus,
	fetchData,
	giftLineBody,
	giftLineKey,
	readGuestGifts,
	writeGuestGifts,
} from '@quizzach/utils';

import { ModalShell } from '../core/ModalShell';
import { useToast } from '../core/Toast';

interface IGiftBasket {
	/** Gifts waiting in this browser for a logged-out visitor. */
	guestCount: number;
	/** The saved gifts are on their way to the server cart: the cart page holds
	 *  its first fetch until they arrive. */
	syncing: boolean;
	countOf: (line: GiftLine) => number;
	/** `name` is what the confirmation toast calls the gift. */
	addGift: (line: GiftLine, name?: string) => Promise<boolean>;
	refreshGiftCounts: () => Promise<void>;
	/** Cart for a logged-in user, the sign-in popup for a guest with gifts. */
	openCart: () => void;
}

const GiftBasketContext = createContext<IGiftBasket>({
	guestCount: 0,
	syncing: false,
	countOf: () => 0,
	addGift: async () => false,
	refreshGiftCounts: async () => undefined,
	openCart: () => undefined,
});

export const useGiftBasket = () => useContext(GiftBasketContext);

const SYNC_MAX_TRIES = 3;

/**
 * `rejected`: the server refuses this gift (e.g. the quiz is gone), trying again
 * can't help. `retry`: the connection or the server failed, it may work later.
 */
const postGift = async (line: GiftLine) => {
	try {
		const response = await fetchData<ICart>(
			'/api/cart/items',
			{ method: 'POST', body: JSON.stringify(giftLineBody(line)) },
			{ authRoute: true }
		);
		if (response.status < 300) return 'added';
		const temporary = [401, 408, 429].includes(response.status);
		return response.status < 500 && !temporary ? 'rejected' : 'retry';
	} catch (err) {
		consoleDev('postGift', ConsoleStatus.NET, err);
		return 'retry';
	}
};

const GiftLoginModal = ({ closeModal }: { closeModal: () => void }) => {
	const { t } = useTranslation();

	return (
		<ModalShell
			onClose={closeModal}
			size='sm'
			center
			icon={<UserCircleLight size={52} />}
			title={t('modal.auth.loginRequiredTitle')}
			actions={
				<BtnTeal
					$small
					as={Link}
					to={RoutePath.Login}
					state={{
						redirect: RoutePath.Summary,
						google_referer: RoutePath.Summary,
					}}
					onClick={closeModal}
				>
					{t('modal.auth.loginRegister')}
				</BtnTeal>
			}
		>
			<p style={{ margin: 0 }}>{t('voucherShop.login.text')}</p>
		</ModalShell>
	);
};

export const GiftBasketProvider = ({ children }: { children: ReactNode }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isAuthenticated } = useAuth();
	const { refreshCartCount } = useCartContext();
	const { showToast } = useToast();

	const [guestLines, setGuestLines] = useState<GiftLine[]>(readGuestGifts);
	const [serverCounts, setServerCounts] = useState<Record<string, number>>({});
	const [syncStopped, setSyncStopped] = useState(false);
	const [showLogin, setShowLogin] = useState(false);
	const runningRef = useRef(false);

	const syncing =
		isAuthenticated === true && guestLines.length > 0 && !syncStopped;

	const refreshGiftCounts = useCallback(async () => {
		if (!window.localStorage.getItem('userToken')) {
			setServerCounts({});
			return;
		}
		try {
			const response = await fetchData<ICart>(
				'/api/cart',
				{ method: 'GET' },
				{ authRoute: true }
			);
			if (response.status >= 300 || !response.data) return;
			const counts: Record<string, number> = {};
			response.data.cart_items?.forEach((item) => {
				if (!item.gift) return;
				const key = item.premade_quiz?.uuid
					? `p:${item.premade_quiz.uuid}`
					: `q:${item.number_of_questions}`;
				counts[key] = (counts[key] ?? 0) + 1;
			});
			setServerCounts(counts);
		} catch (err) {
			consoleDev('refreshGiftCounts', ConsoleStatus.NET, err);
		}
	}, []);

	// a mail activation link can open in another tab: keep this one's count true
	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (e.key === GIFT_BASKET_STORAGE_KEY) setGuestLines(readGuestGifts());
		};
		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	}, []);

	const wasAuthenticated = useRef(false);
	useEffect(() => {
		if (isAuthenticated === false) {
			setSyncStopped(false);
			setServerCounts({});
			// gifts that couldn't be moved don't pass to the next account
			if (wasAuthenticated.current) {
				writeGuestGifts([]);
				setGuestLines([]);
			}
		}
		wasAuthenticated.current = isAuthenticated === true;
	}, [isAuthenticated]);

	// after sign-in: one server line per saved gift. A gift the server refuses,
	// or one that keeps failing, is dropped so it can't come back on every page
	// load; the rest stay saved, so a refresh can't add a gift twice.
	useEffect(() => {
		if (!syncing || runningRef.current) return;
		runningRef.current = true;

		const run = async () => {
			const kept: GiftLine[] = [];
			let failed = false;
			for (const [i, line] of guestLines.entries()) {
				const result = await postGift(line);
				if (result !== 'added') failed = true;
				const tries = (line.tries ?? 0) + 1;
				if (result === 'retry' && tries < SYNC_MAX_TRIES) {
					kept.push({ ...line, tries });
				}
				writeGuestGifts(kept.concat(guestLines.slice(i + 1)));
			}
			runningRef.current = false;
			setGuestLines(kept);
			await Promise.all([refreshCartCount(), refreshGiftCounts()]);
			if (!failed) return;
			setSyncStopped(kept.length > 0);
			showToast({
				severity: 'error',
				message: t('toast.problem'),
				description: t('toast.giftNotAddedText'),
				...(kept.length > 0 && {
					actionLabel: t('common.retry'),
					onAction: () => setSyncStopped(false),
				}),
			});
		};
		void run();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [syncing]);

	const openCart = useCallback(() => {
		if (isAuthenticated) navigate(RoutePath.Summary);
		else setShowLogin(true);
	}, [isAuthenticated, navigate]);

	const addGift = useCallback(
		async (line: GiftLine, name?: string) => {
			if (isAuthenticated) {
				if ((await postGift(line)) !== 'added') {
					showToast({
						severity: 'error',
						message: t('toast.problem'),
						description: t('toast.giftNotAddedText'),
						actionLabel: t('common.retry'),
						onAction: () => {
							void addGift(line, name);
						},
					});
					return false;
				}
				const key = giftLineKey(line);
				setServerCounts((c) => ({ ...c, [key]: (c[key] ?? 0) + 1 }));
				void refreshCartCount();
			} else {
				const next = [...readGuestGifts(), line];
				writeGuestGifts(next);
				setGuestLines(next);
			}
			showToast({
				severity: 'success',
				message: t('toast.giftAdded'),
				description: name,
				actionLabel: t('toast.cart'),
				onAction: openCart,
			});
			return true;
		},
		[isAuthenticated, openCart, refreshCartCount, showToast, t]
	);

	const value = useMemo<IGiftBasket>(
		() => ({
			guestCount: isAuthenticated ? 0 : guestLines.length,
			syncing,
			countOf: (line) => {
				const key = giftLineKey(line);
				return (
					(serverCounts[key] ?? 0) +
					guestLines.filter((l) => giftLineKey(l) === key).length
				);
			},
			addGift,
			refreshGiftCounts,
			openCart,
		}),
		[
			addGift,
			guestLines,
			isAuthenticated,
			openCart,
			refreshGiftCounts,
			serverCounts,
			syncing,
		]
	);

	return (
		<GiftBasketContext.Provider value={value}>
			{children}
			{showLogin && <GiftLoginModal closeModal={() => setShowLogin(false)} />}
		</GiftBasketContext.Provider>
	);
};
